import React, { useState } from "react";
import { useSiteConfig } from "components/Site/Contexts/SiteConfig";
import { useUserSettings } from "components/Site/Contexts/UserSettings";
import { TabSelectDropdownMenu } from "../TabSelectDropdownMenu/TabSelectDropdownMenu";
import { TabSelectMenuListPane } from "../TabSelectDropdownMenu/TabSelectMenuListPane";
import { getMaxTabMenuHeight } from "../utils";
import { ListItem, BibleListItem } from "../ListItems";
import { StateBible } from "components/Bible/BibleContainer";
import BookLibraryIcon from "icons/BookLibraryIcon";
import EcologyGlobeMessage1Icon from "icons/EcologyGlobeMessage1Icon";
import RatingStarIcon from "icons/RatingStarIcon";

interface AllBiblesListProps {
  bibles: StateBible[];
  currentBibleId: string;
  onBibleClick: (bibleId: string) => void;
  siteBibleIds: string[];
}

interface LanguageList {
  id: string;
  name: string;
  nameLocal: string;
}

export const AllBiblesList = ({
  bibles,
  currentBibleId,
  onBibleClick,
  siteBibleIds
}: AllBiblesListProps) => {
  const { title: siteTitle } = useSiteConfig();
  const { darkMode } = useUserSettings();
  const iconColor = darkMode ? "white" : "black";

  const [activeMenuKey, setActiveMenuKey] = useState("bibles");
  const [currentLanguageId, setCurrentLanguageId] = useState("site");
  const languages = Object.values(bibles).reduce((languages, { language }) => {
    if (language && !languages[language.id]) {
      languages[language.id] = {
        id: language.id,
        name: language.name,
        nameLocal: language.nameLocal
      };
    }
    return languages;
  }, {} as { [langId: string]: LanguageList });

  const sortedLanguageArray = Object.values(languages).sort((a, b) => {
    const nameA = a.nameLocal.toUpperCase(); // ignore upper and lowercase
    const nameB = b.nameLocal.toUpperCase();
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    return 0;
  });

  // sorted by romanized `name`, displayed by `localName` in native script
  const languageMenu = sortedLanguageArray.map(({ id, name, nameLocal }) => ({
    key: id,
    onSelect: () => {
      setCurrentLanguageId(id);
      setActiveMenuKey("bibles");
    },
    searchValues: [id, name, nameLocal],
    render: (props: any) => <ListItem {...props}>{nameLocal}</ListItem>
  }));

  languageMenu.unshift({
    key: "site",
    onSelect: () => {
      setCurrentLanguageId("site");
      setActiveMenuKey("bibles");
    },
    searchValues: [siteTitle],
    render: props => (
      <ListItem {...props}>
        <RatingStarIcon size={22} style={{ marginRight: 8 }} /> {siteTitle}
      </ListItem>
    )
  });

  let bibleItems: StateBible[];
  if (currentLanguageId === "site") {
    bibleItems = siteBibleIds
      .map(id => bibles.find(bible => bible.id === id))
      .filter(x => x) as StateBible[];
  } else {
    bibleItems = ([...bibles.filter(x => x)] as StateBible[])
      .filter(
        bible => !currentLanguageId || bible.language?.id === currentLanguageId
      )
      .sort((bibleA, bibleB) =>
        (bibleA.name || "").localeCompare(bibleB.name || "")
      );
  }

  const bibleMenu = bibleItems.map(bible => ({
    key: bible.id,
    onSelect: () => onBibleClick(bible.id),
    searchValues: [bible.name, bible.abbreviationLocal],
    render: (props: any) => <BibleListItem {...props} bible={bible} />
  }));

  const maxListHeight = getMaxTabMenuHeight();
  return (
    <TabSelectDropdownMenu
      activeMenuKey={activeMenuKey}
      onTabClick={(menuKey: string) => setActiveMenuKey(menuKey)}
      menus={[
        {
          tab: <EcologyGlobeMessage1Icon size={24} color={iconColor} />,
          tabPaneComponent: (props: any) => (
            <TabSelectMenuListPane {...props} maxListHeight={maxListHeight} />
          ),
          key: "languages",
          activeMenuItemIndex: languageMenu.findIndex(
            ({ key }: { key: string }) => key === currentLanguageId
          ),
          menuItems: languageMenu
        },
        {
          tab: <BookLibraryIcon size={24} color={iconColor} />,
          tabPaneComponent: (props: any) => (
            <TabSelectMenuListPane {...props} maxListHeight={maxListHeight} />
          ),
          key: "bibles",
          activeMenuItemIndex: bibleMenu.findIndex(
            ({ key }: { key: string }) => key === currentBibleId
          ),
          menuItems: bibleMenu
        }
      ]}
    />
  );
};
